/*
  Tonery — standalone product form, quantity input, and cart drawer.
  No Dawn dependencies. Uses /cart/add.js, /cart/change.js and Shopify's
  Section Rendering API (sections=tny-cart-drawer) to re-render the drawer.
*/
(function () {
  if (window.__tnyCartInit) return;   // guard against double-load
  window.__tnyCartInit = true;

  var ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  var CHANGE_URL = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  var SECTION_ID = 'tny-cart-drawer';

  function drawerEl() { return document.querySelector('cart-drawer'); }

  function refreshBadge(cart) {
    var set = function (c) {
      document.querySelectorAll('[data-cart-count], .header .badge').forEach(function (b) {
        b.textContent = c.item_count;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: c }));
    };
    if (cart && typeof cart.item_count === 'number') return set(cart);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); }).then(set).catch(function () {});
  }

  // Pull the drawer's inner HTML out of a Section Rendering API payload.
  function extractDrawerInner(sectionHTML) {
    try {
      var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
      var inner = doc.querySelector('[data-drawer-inner]');
      return inner ? inner.innerHTML : null;
    } catch (e) { return null; }
  }

  /* ---- <quantity-input> (product cards / product page) ---- */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        var minus = this.querySelector('[name="minus"], .qminus');
        var plus = this.querySelector('[name="plus"], .qplus');
        var self = this;
        if (minus) minus.addEventListener('click', function (e) { e.preventDefault(); self.step(-1); });
        if (plus) plus.addEventListener('click', function (e) { e.preventDefault(); self.step(1); });
      }
      step(dir) {
        if (!this.input) return;
        var min = parseInt(this.input.min) || 1;
        var stepv = parseInt(this.input.step) || 1;
        var val = (parseInt(this.input.value) || min) + dir * stepv;
        if (val < min) val = min;
        this.input.value = val;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ---- <cart-drawer> ---- */
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', class CartDrawer extends HTMLElement {
      connectedCallback() {
        this.inner = this.querySelector('[data-drawer-inner]');
        this.bind();
        var self = this;
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.classList.contains('is-open')) self.close();
        });
      }

      bind() {
        var self = this;
        this.addEventListener('click', function (e) {
          if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); self.close(); return; }
          var qbtn = e.target.closest('[data-qty-change]');
          if (qbtn) {
            var row = qbtn.closest('[data-line-key]');
            var input = row && row.querySelector('[data-qty-input]');
            if (input) {
              var next = (parseInt(input.value) || 0) + parseInt(qbtn.dataset.qtyChange);
              if (next < 0) next = 0;
              self.change(row.dataset.lineKey, next);
            }
            return;
          }
          var rm = e.target.closest('[data-remove]');
          if (rm) { e.preventDefault(); self.change(rm.dataset.lineKey, 0); }
        });
        this.addEventListener('change', function (e) {
          var input = e.target.closest('[data-qty-input]');
          if (input) self.change(input.dataset.lineKey, Math.max(0, parseInt(input.value) || 0));
        });
      }

      // Find the container your CSS animates.
      container() {
        if (this.classList.contains('cart-drawer-container')) return this;
        return this.querySelector('.cart-drawer-container') ||
               this.closest('.cart-drawer-container');
      }

      open() {
        this.inner = this.querySelector('[data-drawer-inner]');
        this.classList.add('is-open');
        document.body.classList.add('cart-drawer-open');
        this.setAttribute('aria-hidden', 'false');

        var container = this.container();
        if (container) {
          container.classList.add('active');
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              container.classList.add('animate');
            });
          });
        }

        var panel = this.querySelector('.cart-drawer__panel');
        if (panel) panel.focus();
      }

      close() {
        this.classList.remove('is-open');
        document.body.classList.remove('cart-drawer-open');
        this.setAttribute('aria-hidden', 'true');

        var container = this.container();
        if (container) {
          container.classList.remove('animate');
          var done = function () {
            container.classList.remove('active');
            container.removeEventListener('transitionend', done);
          };
          container.addEventListener('transitionend', done);
          setTimeout(function () { container.classList.remove('active'); }, 400);
        }
      }

      setBusy(on) { this.classList.toggle('is-busy', !!on); }

      renderFromResponse(data) {
        if (data && data.sections && data.sections[SECTION_ID]) {
          var html = extractDrawerInner(data.sections[SECTION_ID]);
          this.inner = this.querySelector('[data-drawer-inner]');
          if (html !== null && this.inner) this.inner.innerHTML = html;
        }
      }

      change(lineKey, quantity) {
        var self = this;
        this.setBusy(true);
        fetch(CHANGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: quantity, sections: SECTION_ID, sections_url: window.location.pathname })
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            self.renderFromResponse(cart);
            refreshBadge(cart);
          })
          .catch(function () {})
          .finally(function () { self.setBusy(false); });
      }
    });
  }

  /* ---- <product-form> ---- */
  if (!customElements.get('product-form')) {
    customElements.define('product-form', class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        var idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        this.submitButton = this.querySelector('[type="submit"]');
        this.spinner = this.querySelector('.loading-overlay__spinner');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      onSubmit(evt) {
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.clearError();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        if (this.spinner) this.spinner.classList.remove('hidden');

        var formData = new FormData(this.form);
        var drawer = drawerEl();
        if (drawer) {
          formData.append('sections', SECTION_ID);
          formData.append('sections_url', window.location.pathname);
        }

        var self = this;
        fetch(ADD_URL, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        })
          .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
          .then(function (res) {
            if (!res.ok || res.data.status) {
              self.showError((res.data && (res.data.description || res.data.message)) || 'Could not add to cart.');
              return;
            }
            refreshBadge();
            if (drawer) {
              drawer.renderFromResponse(res.data);
              drawer.open();
            } else {
              self.flashAdded();
            }
          })
          .catch(function () { self.showError('Network error. Please try again.'); })
          .finally(function () {
            self.submitButton.classList.remove('loading');
            self.submitButton.removeAttribute('aria-disabled');
            if (self.spinner) self.spinner.classList.add('hidden');
          });
      }

      flashAdded() {
        var span = this.submitButton.querySelector('span') || this.submitButton;
        var original = span.textContent;
        span.textContent = 'Added \u2713';
        this.submitButton.classList.add('added');
        var self = this;
        setTimeout(function () { span.textContent = original; self.submitButton.classList.remove('added'); }, 1200);
      }

      showError(message) {
        if (this.hideErrors) return;
        var el = this.querySelector('.tny-form-error');
        if (!el) { el = document.createElement('div'); el.className = 'tny-form-error'; this.appendChild(el); }
        el.textContent = message;
      }
      clearError() { var el = this.querySelector('.tny-form-error'); if (el) el.textContent = ''; }
    });
  }

  /* ---- open drawer from the header cart icon ---- */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cart-open]');
    if (!opener) return;
    var drawer = drawerEl();
    if (drawer) { e.preventDefault(); drawer.open(); }
  });
})();